import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import { css } from "@emotion/css";
import { healthFormsApi } from "../api";
import {
  parseCSV,
  isSectionHeader,
  expandJointPlaceholder,
  rowToQuestion,
  validateCSVRows,
  parseVisibility,
} from "../utils/csvParser";

interface Section {
  name: string;
  questions: Array<{
    id: number;
    semanticKey: string;
    visibility: "visible" | { visibleWhen: string };
  }>;
}

export const ImportCsv: React.VFC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [testMode, setTestMode] = useState(true); // Default to test mode ON
  const [cancelRequested, setCancelRequested] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll logs to bottom
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, message]);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && selectedFile.name.endsWith(".csv")) {
      setFile(selectedFile);
      setLogs([`Selected file: ${selectedFile.name}`]);
    } else {
      alert("Please select a CSV file");
    }
  };

  const handleClear = () => {
    setFile(null);
    setLogs([]);
    setTestMode(true); // Reset to test mode
    setCancelRequested(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCancel = () => {
    setCancelRequested(true);
    addLog("\n⚠️  Cancellation requested... stopping after current operation");
  };

  const handleDeleteAll = async () => {
    setShowDeleteConfirm(false);
    setIsDeleting(true);
    addLog("\n🗑️  Starting deletion of all questions, groups, and books...\n");

    try {
      // Fetch all entities
      addLog("Fetching all books...");
      const books = await healthFormsApi.books.list();
      addLog(`Found ${books.length} books`);

      addLog("Fetching all question groups...");
      const groups = await healthFormsApi.questionGroups.list();
      addLog(`Found ${groups.length} question groups`);

      addLog("Fetching all questions...");
      const questions = await healthFormsApi.questions.list();
      addLog(`Found ${questions.length} questions\n`);

      let booksDeleted = 0;
      let booksFailed = 0;
      let groupsDeleted = 0;
      let groupsFailed = 0;
      let questionsDeleted = 0;
      let questionsFailed = 0;

      // Delete books first
      if (books.length > 0) {
        addLog("=== Deleting Books ===");
        for (const book of books) {
          try {
            await healthFormsApi.books.delete(book.id);
            addLog(`✓ Deleted book: ${book.name} (ID: ${book.id})`);
            booksDeleted++;
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : "Unknown error";
            addLog(`✗ Failed to delete book ${book.id}: ${errorMsg}`);
            booksFailed++;
          }
        }
        addLog("");
      }

      // Delete question groups
      if (groups.length > 0) {
        addLog("=== Deleting Question Groups ===");
        for (const group of groups) {
          try {
            await healthFormsApi.questionGroups.delete(group.id);
            addLog(`✓ Deleted group: ${group.name} (ID: ${group.id})`);
            groupsDeleted++;
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : "Unknown error";
            addLog(`✗ Failed to delete group ${group.id}: ${errorMsg}`);
            groupsFailed++;
          }
        }
        addLog("");
      }

      // Delete questions
      if (questions.length > 0) {
        addLog("=== Deleting Questions ===");
        for (const question of questions) {
          try {
            await healthFormsApi.questions.delete(question.id);
            addLog(
              `✓ Deleted question: ${question.semanticKey || question.question.primary.substring(0, 40)} (ID: ${question.id})`
            );
            questionsDeleted++;
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : "Unknown error";
            addLog(`✗ Failed to delete question ${question.id}: ${errorMsg}`);
            questionsFailed++;
          }
        }
        addLog("");
      }

      addLog("=== Deletion Complete ===");
      addLog(`Books: ${booksDeleted} deleted, ${booksFailed} failed`);
      addLog(`Question Groups: ${groupsDeleted} deleted, ${groupsFailed} failed`);
      addLog(`Questions: ${questionsDeleted} deleted, ${questionsFailed} failed`);
      addLog(`\nTotal: ${booksDeleted + groupsDeleted + questionsDeleted} entities deleted`);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      addLog(`\n❌ Deletion failed: ${errorMsg}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setIsImporting(true);
    setCancelRequested(false);

    if (testMode) {
      addLog("🧪 TEST MODE - No data will be posted to API\n");
    } else {
      addLog("⚠️  LIVE MODE - Data will be created in database\n");
    }

    addLog("Reading CSV file...");

    try {
      const content = await file.text();
      const rows = parseCSV(content);
      addLog(`Parsed ${rows.length} rows`);

      // Validation step
      addLog("\n=== Validating CSV ===");
      const validation = validateCSVRows(rows);

      if (!validation.valid) {
        addLog(`❌ Validation failed with ${validation.errors.length} error(s):`);
        validation.errors.forEach(err => {
          addLog(`  Row ${err.row}, ${err.field}: ${err.error}`);
        });
        addLog("\nImport aborted. Please fix validation errors and try again.");
        setIsImporting(false);
        return;
      }

      addLog("✓ Validation passed");
      addLog("");

      // Parse rows into questions and sections
      const sections: Section[] = [];
      const questionsToCreate: Array<
        ReturnType<typeof rowToQuestion> & {
          _sectionRef: Section | null;
          _visibility: "visible" | { visibleWhen: string };
        }
      > = [];
      let currentSection: Section | null = null;

      for (const row of rows) {
        if (isSectionHeader(row)) {
          currentSection = { name: row.name, questions: [] };
          sections.push(currentSection);
          addLog(`\n--- Section: ${row.name} ---`);
          continue;
        }

        // Skip empty rows
        if (!row.name && !row.labelEN) continue;

        const expandedRows = expandJointPlaceholder(row);
        for (const expRow of expandedRows) {
          const question = rowToQuestion(expRow);
          const visibility = parseVisibility(expRow.showIf);
          questionsToCreate.push({
            ...question,
            _sectionRef: currentSection,
            _visibility: visibility,
          });
        }
      }

      addLog(`Prepared ${questionsToCreate.length} questions to import\n`);

      // Import questions
      let created = 0;
      let failed = 0;

      for (let i = 0; i < questionsToCreate.length; i++) {
        // Check for cancellation
        if (cancelRequested) {
          addLog("\n❌ Import cancelled by user");
          break;
        }

        const questionData = questionsToCreate[i];
        const sectionRef = questionData._sectionRef;
        const visibility = questionData._visibility;
        const semanticKey = questionData.semanticKey || "";

        // Remove temporary fields
        const { _sectionRef, _visibility, ...cleanQuestionData } = questionData;

        try {
          const responseTypeStr = cleanQuestionData.responseType._type;
          const hasDescription = cleanQuestionData.description ? " (has description)" : "";
          const visibilityStr = visibility === "visible" ? "always visible" : `conditional: ${visibility.visibleWhen}`;

          if (testMode) {
            // Test mode - simulate success (no ID since backend assigns it)
            addLog(`✓ [TEST] Would create: ${semanticKey || "(no key)"}`);
            addLog(`  Type: ${responseTypeStr}${hasDescription}`);
            addLog(`  Visibility: ${visibilityStr}`);
            if (cleanQuestionData.description) {
              addLog(`  Description: ${cleanQuestionData.description}`);
            }

            // Track in section for test mode (use index as mock ID)
            if (sectionRef) {
              sectionRef.questions.push({
                id: 1000 + i, // Mock ID for test mode
                semanticKey,
                visibility,
              });
            }
          } else {
            // Live mode - actually create
            const result = await healthFormsApi.questions.create(cleanQuestionData);
            const realId = result.id;
            addLog(`✓ Created: ${semanticKey || "(no key)"} (ID: ${realId})`);
            addLog(`  Type: ${responseTypeStr}${hasDescription}`);
            addLog(`  Visibility: ${visibilityStr}`);
            if (cleanQuestionData.description) {
              addLog(`  Description: ${cleanQuestionData.description}`);
            }

            // Track in section
            if (sectionRef) {
              sectionRef.questions.push({
                id: realId,
                semanticKey,
                visibility,
              });
            }
          }

          created++;
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : "Unknown error";
          addLog(`✗ Failed: ${semanticKey || "(no key)"} - ${errorMsg}`);
          failed++;
        }
      }

      addLog(`\n=== Questions Import Complete ===`);
      addLog(`Created: ${created}`);
      addLog(`Failed: ${failed}`);

      // Group questions by prefix (before __) within each section
      addLog(`\n=== Creating Question Groups ===`);
      const allGroupsMap: Record<string, { id: number; name: string }> = {};

      for (const section of sections) {
        // Check for cancellation
        if (cancelRequested) {
          addLog("\n❌ Import cancelled - skipping remaining operations");
          setIsImporting(false);
          return;
        }
        // Group questions by prefix before __
        const groupMap: Record<
          string,
          Array<{
            id: number;
            semanticKey: string;
            visibility: "visible" | { visibleWhen: string };
          }>
        > = {};

        for (const q of section.questions) {
          if (q.semanticKey.includes("__")) {
            const prefix = q.semanticKey.substring(0, q.semanticKey.lastIndexOf("__"));
            if (!groupMap[prefix]) {
              groupMap[prefix] = [];
            }
            groupMap[prefix].push(q);
          }
        }

        // Create QuestionGroups for each prefix
        for (const [groupName, questions] of Object.entries(groupMap)) {
          // Check for cancellation
          if (cancelRequested) {
            break;
          }

          try {
            if (testMode) {
              // Test mode - simulate success
              const mockGroupId = Object.keys(allGroupsMap).length + 2000;
              allGroupsMap[groupName] = { id: mockGroupId, name: groupName };
              addLog(`✓ [TEST] Would create group: ${groupName} (${questions.length} questions)`);
            } else {
              // Live mode - actually create
              const group = await healthFormsApi.questionGroups.create({
                _type: "question_group",
                name: groupName,
                questions: questions.map(q => ({
                  questionId: q.id,
                  visibility: q.visibility,
                  requirement: "optional" as const,
                })),
              });
              const realGroupId = group.id;
              allGroupsMap[groupName] = { id: realGroupId, name: groupName };
              addLog(`✓ Created group: ${groupName} (ID: ${realGroupId}, ${questions.length} questions)`);
            }
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : "Unknown error";
            addLog(`✗ Failed to create group: ${groupName} - ${errorMsg}`);
          }
        }
      }

      // Create book with sections as pages
      if (sections.length > 0 && !cancelRequested) {
        addLog(`\n=== Creating Book ===`);
        try {
          const pages = sections
            .filter(s => s.questions.length > 0)
            .map(section => {
              const parts: Array<
                | {
                    _type: "question";
                    questionId: number;
                    visibility: "visible" | { visibleWhen: string };
                    requirement: "optional";
                  }
                | {
                    _type: "question_group";
                    visibility: "visible" | { visibleWhen: string };
                    questionGroup: number;
                  }
              > = [];

              // Track which questions are in groups
              const questionsInGroups = new Set<number>();
              for (const [groupName, groupInfo] of Object.entries(allGroupsMap)) {
                const groupQuestions = section.questions.filter(q => q.semanticKey.startsWith(groupName + "__"));
                if (groupQuestions.length > 0) {
                  // Add group part (use first question's visibility for the group)
                  const groupVisibility = groupQuestions[0]?.visibility || "visible";
                  parts.push({
                    _type: "question_group" as const,
                    visibility: groupVisibility,
                    questionGroup: groupInfo.id,
                  });
                  // Mark these questions as grouped
                  groupQuestions.forEach(q => questionsInGroups.add(q.id));
                }
              }

              // Add standalone questions (not in any group)
              for (const q of section.questions) {
                if (!questionsInGroups.has(q.id)) {
                  parts.push({
                    _type: "question" as const,
                    questionId: q.id,
                    visibility: q.visibility,
                    requirement: "optional" as const,
                  });
                }
              }

              return {
                title: { primary: section.name },
                parts,
              };
            });

          if (testMode) {
            // Test mode - simulate success
            addLog(`✓ [TEST] Would create book: ${file.name.replace(".csv", "")}`);
            addLog(`  - ${pages.length} pages (sections)`);
            pages.forEach(page => {
              const groupCount = page.parts.filter(p => p._type === "question_group").length;
              const questionCount = page.parts.filter(p => p._type === "question").length;
              addLog(`    • ${page.title.primary}: ${groupCount} groups, ${questionCount} standalone questions`);
            });
          } else {
            // Live mode - actually create
            const book = await healthFormsApi.books.create({
              _type: "book",
              name: file.name.replace(".csv", ""),
              description: { primary: `Imported from ${file.name}` },
              pages,
            });

            addLog(`✓ Created book: ${file.name.replace(".csv", "")} (ID: ${book.id})`);
            addLog(`  - ${pages.length} pages (sections)`);
            pages.forEach(page => {
              const groupCount = page.parts.filter(p => p._type === "question_group").length;
              const questionCount = page.parts.filter(p => p._type === "question").length;
              addLog(`    • ${page.title.primary}: ${groupCount} groups, ${questionCount} standalone questions`);
            });
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : "Unknown error";
          addLog(`✗ Failed to create book: ${errorMsg}`);
        }
      }

      if (!cancelRequested) {
        addLog(`\n=== Import Complete ===`);
        if (testMode) {
          addLog(`\n✅ Test run complete! No data was created.`);
          addLog(`To create data for real, uncheck "Test mode" and run again.`);
        } else {
          addLog(`\nYou can now view the imported questions, groups, and book in the respective tabs.`);
        }
      } else {
        addLog(`\n=== Import Cancelled ===`);
        addLog(`Partial import completed: ${created} questions created`);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      addLog(`\n❌ Import failed: ${errorMsg}`);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          Import Questions from CSV
        </Typography>

        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isImporting || isDeleting}
          className={styles.deleteButton}
        >
          {isDeleting ? "Deleting..." : "Delete All Data"}
        </Button>
      </Box>

      <Box className={styles.controls}>
        <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} className={styles.fileInput} />

        <FormControlLabel
          control={
            <Checkbox
              checked={testMode}
              onChange={e => setTestMode(e.target.checked)}
              color="primary"
              disabled={isImporting}
            />
          }
          label="Test mode (no API calls)"
          className={styles.checkbox}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleImport}
          disabled={!file || isImporting}
          className={styles.button}
        >
          {isImporting ? "Importing..." : testMode ? "Test Import" : "Import Questions"}
        </Button>

        {isImporting ? (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCancel}
            disabled={cancelRequested}
            className={styles.button}
          >
            {cancelRequested ? "Cancelling..." : "Cancel"}
          </Button>
        ) : (
          <Button variant="outlined" onClick={handleClear} disabled={isImporting} className={styles.button}>
            Clear
          </Button>
        )}

        {(isImporting || isDeleting) && <CircularProgress size={24} />}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <DialogTitle>Delete All Data?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete ALL questions, question groups, and books from the system. This action cannot
            be undone.
            <br />
            <br />
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteAll} color="secondary" autoFocus>
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      <TextField
        multiline
        rows={25}
        fullWidth
        value={logs.join("\n")}
        placeholder="Import logs will appear here..."
        InputProps={{
          readOnly: true,
          style: { fontFamily: "monospace", fontSize: "12px" },
        }}
        variant="outlined"
        className={styles.logsField}
      />
      <div ref={logsEndRef} />
    </Box>
  );
};

const styles = {
  container: css`
    padding: 24px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  `,
  title: css`
    margin: 0;
  `,
  deleteButton: css`
    color: #d32f2f;
    border-color: #d32f2f;
    &:hover {
      background-color: rgba(211, 47, 47, 0.04);
      border-color: #d32f2f;
    }
  `,
  controls: css`
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 16px;
  `,
  fileInput: css`
    flex: 1;
  `,
  checkbox: css`
    white-space: nowrap;
    margin-left: 8px;
  `,
  button: css`
    white-space: nowrap;
  `,
  logsField: css`
    flex: 1;
    & textarea {
      font-family: "Monaco", "Courier New", monospace;
      font-size: 12px;
      line-height: 1.4;
    }
  `,
};

export default ImportCsv;
